import React, { useState, useEffect } from 'react';
import { useGameStore, selectCurrentLesson, selectCurrentSentence } from '../store/gameStore';
import { useAuthStore, isAdmin } from '../store/authStore';
import { lessonsApi } from '../api/lessons';

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: '#0f172a',
  border: '1px solid #334155',
  borderRadius: '6px',
  color: '#e2e8f0',
  fontSize: '0.9rem',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  marginBottom: '4px',
  fontSize: '0.72rem',
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const btn = (bg, color = 'white') => ({
  padding: '8px 18px',
  background: bg,
  color,
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer',
});

const LessonEditor = ({ open, onClose }) => {
  const { user } = useAuthStore();
  const currentLesson = useGameStore(selectCurrentLesson);
  const currentSentence = useGameStore(selectCurrentSentence);
  const { currentLessonIndex, lessons, updateLesson, currentSentenceIndex } = useGameStore();
  
  // Local editable copies of all characters in the current sentence
  const [drafts, setDrafts] = useState([]);
  const [dialogueDraft, setDialogueDraft] = useState({ pinyin: '', english: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedMsg, setSavedMsg] = useState(null);
  
  // Build drafts when modal opens or current sentence changes
  useEffect(() => {
    if (!open || !currentSentence) return;
    setDrafts((currentSentence.breakdown || []).map((c) => ({
      id: c.id,
      char: c.char,
      pinyin: c.pinyin || '',
      radical: c.radical || '',
      radical_meaning: c.radical_meaning || c.radicalMeaning || '',
      meaning: c.meaning || '',
    })));
    setDialogueDraft({
      pinyin: currentSentence.pinyin || '',
      english: currentSentence.english || '',
    });
    setError(null);
    setSavedMsg(null);
  }, [open, currentSentence?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  
  if (!open) return null;
  
  const canEdit = !!user && (
    isAdmin(user) ||
    currentLesson?.createdById === user.id ||
    (currentLesson?.createdById === null && isAdmin(user)) // only admins edit official content
  );
  
  if (!canEdit) {
    return (
      <Overlay onClose={onClose}>
        <h2 style={{ marginTop: 0, color: '#f1f5f9' }}>Edit Lesson</h2>
        <p style={{ color: '#94a3b8' }}>
          {!user
            ? 'You must be signed in to edit lessons.'
            : currentLesson?.createdById === null
              ? 'Official lessons can only be edited by admins.'
              : 'You can only edit lessons you created.'}
        </p>
        <div style={{ textAlign: 'right' }}>
          <button onClick={onClose} style={btn('#334155', '#e2e8f0')}>Close</button>
        </div>
      </Overlay>
    );
  }
  
  const updateDraft = (index, field, value) => {
    const next = [...drafts];
    next[index] = { ...next[index], [field]: value };
    setDrafts(next);
  };
  
  const handleSave = async () => {
    if (!currentSentence) return;
    setSaving(true);
    setError(null);
    setSavedMsg(null);
    
    try {
      // Find original character data to compute diffs
      const originals = currentSentence.breakdown || [];
      const charPromises = drafts.map(async (draft, i) => {
        const orig = originals[i];
        if (!orig) return null;
        
        const changes = {};
        if (draft.pinyin !== (orig.pinyin || '')) changes.pinyin = draft.pinyin;
        if (draft.radical !== (orig.radical || '')) changes.radical = draft.radical;
        if (draft.radical_meaning !== (orig.radical_meaning || '')) {
          changes.radicalMeaning = draft.radical_meaning;
        }
        if (draft.meaning !== (orig.meaning || '')) changes.meaning = draft.meaning;
        
        if (Object.keys(changes).length === 0) return null;
        return lessonsApi.updateCharacter(draft.id, changes);
      });
      
      // Dialogue-level changes
      const dialogueChanges = {};
      if (dialogueDraft.pinyin !== (currentSentence.pinyin || '')) {
        dialogueChanges.pinyin = dialogueDraft.pinyin;
      }
      if (dialogueDraft.english !== (currentSentence.english || '')) {
        dialogueChanges.english = dialogueDraft.english;
      }
      const dialoguePromise = Object.keys(dialogueChanges).length > 0
        ? lessonsApi.updateDialogue(currentSentence.id, dialogueChanges)
        : Promise.resolve(null);
      
      await Promise.all([...charPromises, dialoguePromise]);
      
      // Merge changes back into the in-memory lesson so the UI updates immediately
      const updatedLesson = JSON.parse(JSON.stringify(currentLesson));
      const updatedDialogue = updatedLesson.dialogue[currentSentenceIndex];
      if (dialogueChanges.pinyin !== undefined) updatedDialogue.pinyin = dialogueChanges.pinyin;
      if (dialogueChanges.english !== undefined) updatedDialogue.english = dialogueChanges.english;
      updatedDialogue.breakdown = drafts.map((d) => ({
        id: d.id,
        char: d.char,
        pinyin: d.pinyin,
        radical: d.radical,
        radical_meaning: d.radical_meaning,
        meaning: d.meaning,
      }));
      updateLesson(currentLessonIndex, updatedLesson);
      
      setSavedMsg('Saved ✓');
      setTimeout(() => setSavedMsg(null), 2000);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Overlay onClose={onClose} wide>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#f1f5f9' }}>✏️ Edit Sentence</h2>
          <div style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: '4px' }}>
            {currentLesson?.code} — {currentLesson?.title}
          </div>
        </div>
        <button onClick={onClose} style={{ ...btn('#334155', '#e2e8f0'), padding: '6px 12px' }}>✕</button>
      </div>
      
      {/* Sentence-level fields */}
      <div style={{ background: '#0f172a', padding: '12px', borderRadius: '10px', marginBottom: '16px' }}>
        <div style={{ fontSize: '1.6rem', marginBottom: '12px', textAlign: 'center', letterSpacing: '0.05em' }}>
          {currentSentence?.chinese}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <label style={labelStyle}>Pinyin (full sentence)</label>
            <input
              style={inputStyle}
              value={dialogueDraft.pinyin}
              onChange={(e) => setDialogueDraft({ ...dialogueDraft, pinyin: e.target.value })}
            />
          </div>
          <div>
            <label style={labelStyle}>English</label>
            <input
              style={inputStyle}
              value={dialogueDraft.english}
              onChange={(e) => setDialogueDraft({ ...dialogueDraft, english: e.target.value })}
            />
          </div>
        </div>
      </div>
      
      {/* Per-character editor */}
      <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Per-character attributes
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '50vh', overflowY: 'auto', paddingRight: '6px' }}>
        {drafts.map((d, i) => (
          <div key={d.id} style={{
            background: '#0f172a',
            borderRadius: '10px',
            padding: '10px 12px',
            display: 'grid',
            gridTemplateColumns: '48px 1fr 1fr 1fr 1fr',
            gap: '8px',
            alignItems: 'end',
          }}>
            <div style={{ fontSize: '2rem', textAlign: 'center', lineHeight: 1 }}>{d.char}</div>
            <div>
              <label style={labelStyle}>Pinyin</label>
              <input style={inputStyle} value={d.pinyin} onChange={(e) => updateDraft(i, 'pinyin', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Radical</label>
              <input style={inputStyle} value={d.radical} onChange={(e) => updateDraft(i, 'radical', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Radical Meaning</label>
              <input style={inputStyle} value={d.radical_meaning} onChange={(e) => updateDraft(i, 'radical_meaning', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Meaning</label>
              <input style={inputStyle} value={d.meaning} onChange={(e) => updateDraft(i, 'meaning', e.target.value)} />
            </div>
          </div>
        ))}
      </div>
      
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid #ef4444',
          color: '#ef4444',
          padding: '10px',
          borderRadius: '8px',
          marginTop: '12px',
          fontSize: '0.85rem',
        }}>
          {error}
        </div>
      )}
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
        <div style={{ color: '#22c55e', fontSize: '0.9rem' }}>{savedMsg}</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={onClose} style={btn('#334155', '#e2e8f0')}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={btn(saving ? '#4f46e5aa' : '#6366f1')}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </Overlay>
  );
};

const Overlay = ({ children, onClose, wide }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      zIndex: 100,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '14px',
        padding: '20px',
        width: '100%',
        maxWidth: wide ? '900px' : '460px',
        maxHeight: '90vh',
        overflow: 'auto',
        color: '#e2e8f0',
      }}
    >
      {children}
    </div>
  </div>
);

export default LessonEditor;
